import { cn } from '@respectly/ui';
import { Circle, Clock, CheckCircle2, Archive } from 'lucide-react';
import type { SupportRequestStatus } from '@respectly/shared-types';

interface SupportStatusBadgeProps {
  status: SupportRequestStatus;
  className?: string;
  showDot?: boolean;
  size?: 'sm' | 'md';
}

const statusConfig: Record<
  SupportRequestStatus,
  { label: string; icon: React.ElementType; classes: string }
> = {
  open: {
    label: 'Open',
    icon: Circle,
    classes:
      'bg-blue-500/10 text-blue-600 dark:text-blue-400 ring-1 ring-inset ring-blue-500/25',
  },
  in_progress: {
    label: 'In Progress',
    icon: Clock,
    classes:
      'bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-1 ring-inset ring-amber-500/25',
  },
  resolved: {
    label: 'Resolved',
    icon: CheckCircle2,
    classes:
      'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-inset ring-emerald-500/25',
  },
  closed: {
    label: 'Closed',
    icon: Archive,
    classes:
      'bg-zinc-500/10 text-zinc-500 dark:text-zinc-400 ring-1 ring-inset ring-zinc-500/20',
  },
};

export function SupportStatusBadge({
  status,
  className,
  size = 'sm',
}: SupportStatusBadgeProps) {
  const config = statusConfig[status] ?? statusConfig.open;
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        config.classes,
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        className
      )}
    >
      <Icon className={cn(size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
      {config.label}
    </span>
  );
}

export function getSupportStatusColor(status: SupportRequestStatus): string {
  const colors: Record<SupportRequestStatus, string> = {
    open: '#3b82f6',
    in_progress: '#f59e0b',
    resolved: '#10b981',
    closed: '#71717a',
  };
  return colors[status];
}

export function getSupportStatusLabel(status: SupportRequestStatus): string {
  return statusConfig[status].label;
}
